/**
 * Standalone "Send to my benefits administrator" modal.
 *
 * Mounted once at the dashboard level and opened from anywhere via:
 *   window.dispatchEvent(new Event("glpflo:open-benefits-share"));
 *
 * This keeps the form + send logic in one place while letting multiple
 * entry points (BillingPreviewCard, Order History sublink, etc.) share it
 * without prop-drilling or context plumbing.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

export const BENEFITS_SHARE_EVENT = "glpflo:open-benefits-share";

export default function BenefitsShareModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ admin_email: "", admin_name: "", patient_note: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(BENEFITS_SHARE_EVENT, handler);
    return () => window.removeEventListener(BENEFITS_SHARE_EVENT, handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin_email.trim())) {
      toast.error("Please enter a valid admin email");
      return;
    }
    setSending(true);
    try {
      const { data: res } = await API.post("/billing/send-to-administrator", {
        admin_email: form.admin_email.trim(),
        admin_name: form.admin_name.trim(),
        patient_note: form.patient_note.trim(),
      });
      toast.success(`Sent to ${res.to_email}`);
      setOpen(false);
      setForm({ admin_email: "", admin_name: "", patient_note: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the summary");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending) setOpen(o); }}>
      <DialogContent className="sm:max-w-md" data-testid="benefits-share-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-600" /> Send billing summary
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 leading-relaxed">
            We&apos;ll email a PDF directly to your HSA / FSA administrator with your next charge,
            active subscriptions, and the last 12 months of completed payments. They can reply
            straight back to you.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Administrator email
            </label>
            <input
              type="email" required autoFocus
              value={form.admin_email}
              onChange={(e) => setForm((s) => ({ ...s, admin_email: e.target.value }))}
              placeholder="benefits@employer.com"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              data-testid="benefits-share-email"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Their name <span className="font-normal lowercase text-slate-400">(optional)</span>
            </label>
            <input
              type="text" maxLength={80}
              value={form.admin_name}
              onChange={(e) => setForm((s) => ({ ...s, admin_name: e.target.value }))}
              placeholder="Jane Doe"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              data-testid="benefits-share-name"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Note <span className="font-normal lowercase text-slate-400">(optional, 500 char)</span>
            </label>
            <textarea
              rows={3} maxLength={500}
              value={form.patient_note}
              onChange={(e) => setForm((s) => ({ ...s, patient_note: e.target.value }))}
              placeholder="For my 2026 HSA reimbursement filing — thanks!"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              data-testid="benefits-share-note"
            />
            <p className="text-[10px] text-slate-400 text-right">{form.patient_note.length}/500</p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" disabled={sending} onClick={() => setOpen(false)} className="text-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={sending} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm" data-testid="benefits-share-submit">
              {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send PDF</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function openBenefitsShareModal() {
  window.dispatchEvent(new Event(BENEFITS_SHARE_EVENT));
}
